import { reactive, computed, ref, watch, getCurrentInstance, onMounted, onUnmounted } from "vue";
import { getChatterList } from "@/api";
import { LD_FLAG_IDEA_MDP_429 } from "@components/constants/launchDarklyFlagKeys";
import {
  PAY_RANGE_CHATTER_TABLE,
  PAY_RANGE_VERSION_CHATTER_TABLE,
  transformPayRangeChatterResults,
} from "@components/utils/payRangeChatter";
import {
  getAccountId,
  getPayRange,
  publishPayRange,
  savePayRangeDraft,
  createPayRangeVersion,
  editPayRangeVersion,
  discardPayRangeVersionDraft,
  discardPayRangeVersion,
  publishPayRangeVersion,
  publishPayRangeVersionDraft,
  voidPayRangeVersion,
  correctPayRangeVersion,
  payRangeLifecycleAction,
} from "@/api/payRangeAPI";
import { mapPayRangeDetailToCard } from "@components/PayRangeDetailPage/mapPayRangeDetailToCard";
import { payRangeApiToModalInitialValues } from "@components/PayRangeModal/payRangeDraftPayload";
import { FAIR_PAY_TAB, fairPayListRoute } from "@components/FairPayMenu/fairPayMenuConstants";

/**
 * DatePicker emits a Date (or ISO string). Backend DateField needs YYYY-MM-DD.
 * @param {Date|string|null|undefined} value
 * @returns {string|null}
 */
export function toApiDateString(value) {
  if (value == null || value === "") return null;
  if (typeof value === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    // ISO datetime from Date.toJSON / axios-serialized Date
    if (/^\d{4}-\d{2}-\d{2}T/.test(value)) return value.slice(0, 10);
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/** Backend: retire_on must be strictly after current version effective_from. */
export function dayAfterApiDate(value) {
  const iso = toApiDateString(value);
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + 1);
  return toApiDateString(d);
}

export function maxApiDate(a, b) {
  const left = toApiDateString(a);
  const right = toApiDateString(b);
  if (!left) return right;
  if (!right) return left;
  return left >= right ? left : right;
}

/** ER-11664: never send a pay-range write against an account this page's data
 *  was not loaded under — the API is scoped by the account read at call time. */
const ACCOUNT_CONTEXT_CHANGED =
  "Your account changed while this page was open — reloading the pay range";

const VOID_ERROR_MESSAGES = {
  PAY_RANGE_VERSION_LAST_REMAINING: "The last remaining version cannot be voided",
  PAY_RANGE_VERSION_IN_USE: "This version is in use and cannot be voided",
  PAY_RANGE_VERSION_ALREADY_VOIDED: "This version is already voided",
  PAY_RANGE_VERSION_NOT_FOUND: "This version could not be found",
  PAY_RANGE_VERSION_NOT_PUBLISHED: "This version is not published and cannot be corrected",
  PAY_RANGE_VERSION_MAX_NOT_ABOVE_MIN: "Max must be greater than min",
  PAY_RANGE_VERSION_INVALID_WINDOW: "The dates entered are outside the allowed range for this version",
};

function extractApiError(err, fallback) {
  const data = err?.response?.data;
  if (!data) return err?.message || fallback;
  if (typeof data === "string") return data;
  if (data.error) return VOID_ERROR_MESSAGES[data.error] || data.error;
  if (data.detail) return data.detail;
  // DRF field errors: { effective_from: ["Date has wrong format…"] }
  if (typeof data === "object") {
    const firstKey = Object.keys(data)[0];
    const first = firstKey ? data[firstKey] : null;
    if (Array.isArray(first) && first[0]) return String(first[0]);
    if (typeof first === "string") return first;
  }
  return err?.message || fallback;
}

/**
 * Detail page state + wiring for ER-10758.
 * @param {{ payRangeId: import('vue').Ref<string|number>|(() => string|number) }} options
 */
export function usePayRangeDetailPageState(options = {}) {
  const app = getCurrentInstance();
  const { proxy } = app || {};

  const state = reactive({
    loading: true,
    saving: false,
    error: "",
    // Account context `api`/`card` were loaded under — every pay-range request
    // is scoped by the account read at call time (ER-11664).
    accountId: null,
    api: null,
    card: null,
    publishDialog: {
      show: false,
      effectiveFrom: "",
      mode: "first", // 'first' | 'version'
    },
    retireDialog: {
      show: false,
      retireOn: "",
    },
    editModal: {
      show: false,
      mode: "edit",
      payRangeId: null,
      stepNumber: 1,
      initialValues: {},
    },
    versionDraft: {
      show: false,
      version: null,
      replacedVersion: null,
    },
    voidDialog: {
      show: false,
      version: null,
    },
    correctDialog: {
      show: false,
      version: null,
    },
    versionDrawer: {
      show: false,
      key: null,
    },
    chatter: {
      show: false,
      listData: {},
      typeName: "Pay Range",
      typeValue: "",
      iconName: "popup_pay_range",
      versionId: null,
      items: [],
      total: 0,
    },
  });

  /** ER-12490: the version manage drawer ships behind idea-MDP-429, like the other Pay Range screens. */
  const versionDrawerEnabled = ref(false);
  if (app) {
    onMounted(async () => {
      try {
        versionDrawerEnabled.value =
          (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_429, false)) === true;
      } catch {
        versionDrawerEnabled.value = false;
      }
    });
  }

  const payRangeId = computed(() => {
    const raw =
      typeof options.payRangeId === "function"
        ? options.payRangeId()
        : options.payRangeId?.value ?? options.payRangeId;
    return raw;
  });

  /** Rows for PayRangeVersionHistoryList — built by mapPayRangeDetailToCard._meta.history. */
  const versionHistory = computed(() => state.card?._meta?.history || []);

  /** The history row the manage drawer shows — re-resolved on every refresh. */
  const manageVersion = computed(() => {
    const key = state.versionDrawer.key;
    if (key == null) return null;
    return versionHistory.value.find((row) => row.key === key) || null;
  });

  function notifySuccess(message) {
    if (proxy?.$notifications?.success) {
      proxy.$notifications.success(message);
    }
  }

  function notifyError(err, fallback) {
    const message = extractApiError(err, fallback);
    if (proxy?.$notifications?.error) {
      proxy.$notifications.error(message);
    }
  }

  /**
   * Adopt one detail payload as the page state. Shared by the initial load and
   * by writes whose response already is the full detail (ER-12600 Correct).
   */
  function applyDetail(api, accountId = state.accountId) {
    state.api = api || null;
    state.accountId = state.api ? accountId : null;
    state.card = state.api ? mapPayRangeDetailToCard(state.api) : null;
    // A corrected row keeps its key; a version that vanished (void/discard)
    // closes the drawer instead of showing a stale one.
    if (state.versionDrawer.show && !manageVersion.value) closeVersionDrawer();
  }

  async function fetchDetail() {
    const id = payRangeId.value;
    if (!id) {
      state.loading = false;
      state.error = "Missing pay range id";
      state.api = null;
      state.accountId = null;
      state.card = null;
      return;
    }
    state.loading = true;
    state.error = "";
    // Captured alongside the request so the page knows which account its data
    // belongs to (ER-11664).
    const accountId = getAccountId();
    try {
      const response = await getPayRange(id);
      applyDetail(response?.data || null, accountId);
    } catch (err) {
      state.api = null;
      state.accountId = null;
      state.card = null;
      state.error =
        err?.response?.data?.error ||
        err?.message ||
        "Failed to load pay range";
      notifyError(err, "Failed to load pay range");
    } finally {
      state.loading = false;
    }
  }

  /**
   * Is the account context still the one this page's data was loaded under?
   *
   * Every request in `payRangeAPI` is scoped by the account read at call time,
   * while `state.api` was loaded under `state.accountId`. If the context
   * changed while the page stayed mounted, a write would be sent to an account
   * that does not own this pay range — the API answers 404 ("No PayRange
   * matches the given query"), and an id that does exist there would be
   * mutated by mistake. Refuse the write and re-load for the current account
   * instead, so the page can never act on stale context (ER-11664).
   *
   * @returns {boolean} true when the action may be sent.
   */
  function accountContextIsCurrent() {
    if (state.accountId == null) return true;
    if (String(getAccountId()) === String(state.accountId)) return true;
    notifyError(null, ACCOUNT_CONTEXT_CHANGED);
    fetchDetail();
    return false;
  }

  watch(
    payRangeId,
    () => {
      fetchDetail();
    },
    { immediate: true }
  );

  /**
   * Another tab switching account changes the context this page's requests are
   * scoped by, with no navigation to re-mount the page (ER-11664). Re-load so
   * the rendered pay range always belongs to the current account.
   */
  function onAccountContextStored(event) {
    if (event?.key && event.key !== "currentAccountID") return;
    if (state.accountId == null) return;
    if (String(getAccountId()) === String(state.accountId)) return;
    fetchDetail();
  }

  if (getCurrentInstance()) {
    window.addEventListener("storage", onAccountContextStored);
    onUnmounted(() => {
      window.removeEventListener("storage", onAccountContextStored);
    });
  }

  function goToList() {
    if (proxy?.$router) {
      proxy.$router.push(fairPayListRoute(FAIR_PAY_TAB.PAY_RANGES)).catch(() => {});
    }
  }

  // —— First-draft edit (PayRangeModal) ————————————————

  function openEditModal() {
    const api = state.api;
    if (!api) return;
    state.editModal = {
      show: true,
      mode: "edit",
      payRangeId: api.id,
      stepNumber: 1,
      initialValues: payRangeApiToModalInitialValues({
        ...api,
        operating_country: api.operating_country
          ? {
              ...api.operating_country,
              icon: state.card?.flag || api.operating_country.icon,
            }
          : api.operating_country,
      }),
    };
  }

  function closeEditModal() {
    state.editModal = {
      show: false,
      mode: "edit",
      payRangeId: state.api?.id ?? null,
      stepNumber: 1,
      initialValues: {},
    };
  }

  async function handleEditSaveDraft(form) {
    if (!accountContextIsCurrent()) return;
    try {
      await savePayRangeDraft({
        ...form,
        mode: "edit",
        payRangeId: state.api?.id,
      });
      notifySuccess("Draft updated");
      closeEditModal();
      await fetchDetail();
    } catch (err) {
      notifyError(err, "Failed to update draft");
      throw err;
    }
  }

  // —— First-draft Draft drawer (Rule 33) ————————————————
  // The drawer hands its handlers an already-built API body, so these call the endpoints
  // directly: routing it through savePayRangeDraft would map the body a second time.

  /** True once the open drawer saved or published — the page re-loads when it closes. */
  let draftDrawerChanged = false;

  function assertAccountContextIsCurrent() {
    if (!accountContextIsCurrent()) throw new Error(ACCOUNT_CONTEXT_CHANGED);
  }

  async function handleDraftDrawerSave(id, body) {
    assertAccountContextIsCurrent();
    return payRangeLifecycleAction(id, "edit-draft", body);
  }

  async function handleDraftDrawerPublish(id, body) {
    assertAccountContextIsCurrent();
    return publishPayRange(id, body);
  }

  function onDraftDrawerSaved() {
    draftDrawerChanged = true;
  }

  function onDraftDrawerPublished() {
    draftDrawerChanged = true;
    notifySuccess("Pay range published");
  }

  // Re-load only on close: a refresh while open would re-seed the drawer and drop its "Draft saved" alert.
  async function closeDraftDrawer() {
    const changed = draftDrawerChanged;
    draftDrawerChanged = false;
    closeEditModal();
    if (changed) await fetchDetail();
  }

  // —— Publish / Activate (first draft or version draft) ———

  function openPublishDialog(mode = "first") {
    // Version draft publish uses staged effective_from — no date dialog.
    if (mode === "version") {
      confirmPublishVersion();
      return;
    }
    const stagedFrom =
      toApiDateString(state.api?.draft_effective_from) ||
      toApiDateString(state.card?._meta?.draftEffectiveFrom);
    state.publishDialog = {
      show: true,
      effectiveFrom: stagedFrom || toApiDateString(new Date()),
      mode: "first",
    };
  }

  function closePublishDialog() {
    state.publishDialog.show = false;
  }

  async function confirmPublish() {
    const id = state.api?.id;
    const effectiveFrom = toApiDateString(state.publishDialog.effectiveFrom);
    if (!id || !effectiveFrom) return;
    if (!accountContextIsCurrent()) return;
    state.saving = true;
    try {
      await publishPayRange(id, {
        effective_from: effectiveFrom,
      });
      notifySuccess("Pay range published");
      closePublishDialog();
      await fetchDetail();
    } catch (err) {
      notifyError(err, "Failed to publish pay range");
    } finally {
      state.saving = false;
    }
  }

  async function confirmPublishVersion() {
    const id = state.api?.id;
    if (!id) return;
    if (!accountContextIsCurrent()) return;
    state.saving = true;
    try {
      await publishPayRangeVersionDraft(id);
      notifySuccess("Version draft published");
      await fetchDetail();
    } catch (err) {
      notifyError(err, "Failed to publish version draft");
    } finally {
      state.saving = false;
    }
  }

  function openRetireDialog() {
    const today = toApiDateString(new Date());
    const minRetireOn = dayAfterApiDate(state.card?._meta?.effectiveFrom);
    state.retireDialog = {
      show: true,
      retireOn: maxApiDate(today, minRetireOn) || today || "",
    };
  }

  function closeRetireDialog() {
    state.retireDialog.show = false;
  }

  async function confirmRetire() {
    const id = state.api?.id;
    const retireOn = toApiDateString(state.retireDialog.retireOn);
    const minRetireOn = dayAfterApiDate(state.card?._meta?.effectiveFrom);
    if (!id || !retireOn) return;
    if (minRetireOn && retireOn < minRetireOn) return;
    if (!accountContextIsCurrent()) return;
    state.saving = true;
    try {
      await payRangeLifecycleAction(id, "retire", { retire_on: retireOn });
      notifySuccess("Pay range retired");
      closeRetireDialog();
      await fetchDetail();
    } catch (err) {
      notifyError(err, "Failed to retire pay range");
    } finally {
      state.saving = false;
    }
  }

  // —— Version draft drawer (Rule 33: New version / Edit draft) ———————————
  // Same hand-off as the first-draft drawer: re-load only on close, because a refresh
  // while open would re-seed the drawer and drop its "Draft saved" alert.

  /** True once the open drawer saved, published or discarded — the page re-loads when it closes. */
  let versionDraftChanged = false;

  /** The draft to continue editing: a Draft row of `versions[]`, else the legacy staged draft fields. */
  function resolveVersionDraft() {
    const api = state.api;
    if (!api) return null;
    const drafts = (Array.isArray(api.versions) ? api.versions : []).filter(
      (v) => String(v.status || "").toLowerCase() === "draft" && !v.voided_at
    );
    if (drafts.length) return drafts.reduce((a, b) => (Number(b.id) > Number(a.id) ? b : a));
    const meta = state.card?._meta || {};
    if (!meta.hasVersionDraft) return null;
    return {
      id: null,
      min_amount: meta.draftMin ?? null,
      max_amount: meta.draftMax ?? null,
      effective_from: meta.draftEffectiveFrom || null,
    };
  }

  /** Ranges that predate `versions[]` carry the current version only as the active snapshot. */
  function resolveReplacedVersion() {
    const api = state.api;
    const hasLiveVersion = (Array.isArray(api?.versions) ? api.versions : []).some(
      (v) => v.version_number != null && !v.voided_at
    );
    const snap = api?.compensation_snapshot;
    if (hasLiveVersion || !snap) return null;
    return { ...snap, version_number: snap.version_number || 1, active: true };
  }

  function openVersionDraft() {
    if (!state.api) return;
    versionDraftChanged = false;
    state.versionDraft = {
      show: true,
      version: resolveVersionDraft(),
      replacedVersion: resolveReplacedVersion(),
    };
  }

  async function handleVersionDraftSave(body, ctx = {}) {
    assertAccountContextIsCurrent();
    if (ctx.versionId != null) return editPayRangeVersion(ctx.payRangeId, ctx.versionId, body);
    return createPayRangeVersion(ctx.payRangeId, body);
  }

  async function handleVersionDraftPublish(id, versionId) {
    assertAccountContextIsCurrent();
    return publishPayRangeVersion(id, versionId);
  }

  async function handleVersionDraftDiscard(id, versionId) {
    assertAccountContextIsCurrent();
    return discardPayRangeVersion(id, versionId);
  }

  function onVersionDraftSaved() {
    versionDraftChanged = true;
    notifySuccess("Version draft saved");
  }

  function onVersionDraftPublished() {
    versionDraftChanged = true;
    notifySuccess("Version draft published");
  }

  function onVersionDraftDiscarded() {
    // A legacy staged draft has no version id: the drawer had nothing to delete, so nothing was discarded.
    if (state.versionDraft.version?.id == null) return;
    versionDraftChanged = true;
    notifySuccess("Version draft discarded");
  }

  async function closeVersionDraft() {
    const changed = versionDraftChanged;
    versionDraftChanged = false;
    state.versionDraft = { show: false, version: null, replacedVersion: null };
    if (changed) await fetchDetail();
  }

  // —— Discard / lifecycle ————————————————————————

  async function discardDraft() {
    const id = state.api?.id;
    const meta = state.card?._meta || {};
    if (!id) return;
    if (!accountContextIsCurrent()) return;
    // Rule 19: delete-draft is for a DRAFT range only; a Draft version of a PUBLISHED range is
    // removed with discard-version, or discard-version-draft for the legacy staged draft.
    const draftRow = (meta.history || []).find((row) => row.status === "draft");
    const isFirstDraft = meta.status === "DRAFT";
    if (!isFirstDraft && draftRow?.versionId == null && !meta.hasVersionDraft) {
      notifyError(null, "There is no draft version to discard");
      return;
    }
    state.saving = true;
    try {
      if (isFirstDraft) {
        await payRangeLifecycleAction(id, "delete-draft", {}, "delete");
        notifySuccess("Draft deleted");
        goToList();
      } else {
        if (draftRow?.versionId != null) {
          await discardPayRangeVersion(id, draftRow.versionId);
        } else {
          await discardPayRangeVersionDraft(id);
        }
        notifySuccess("Version draft discarded");
        await fetchDetail();
      }
    } catch (err) {
      notifyError(err, "Failed to discard draft");
    } finally {
      state.saving = false;
    }
  }

  /** House-style past-tense success toasts for lifecycle actions (ER-11218). */
  const LIFECYCLE_SUCCESS_MESSAGES = {
    reactivate: "Pay range reactivated",
    archive: "Pay range archived",
    unarchive: "Pay range unarchived",
  };

  async function runLifecycle(action, body = {}) {
    const id = state.api?.id;
    if (!id) return;
    if (!accountContextIsCurrent()) return;
    state.saving = true;
    try {
      await payRangeLifecycleAction(id, action, body);
      notifySuccess(
        LIFECYCLE_SUCCESS_MESSAGES[action] ||
          `Pay range ${action.replace(/-/g, " ")}`
      );
      if (action === "delete-draft") {
        goToList();
      } else {
        await fetchDetail();
      }
    } catch (err) {
      notifyError(err, `Failed to ${action}`);
    } finally {
      state.saving = false;
    }
  }

  // —— Card event handlers ————————————————————————

  function onCardEvent(event) {
    switch (event) {
      case "navigate-list":
        goToList();
        break;
      case "edit":
        openEditModal();
        break;
      case "publish":
        openPublishDialog("first");
        break;
      case "publish-draft":
        openPublishDialog("version");
        break;
      case "discard-draft":
        discardDraft();
        break;
      case "new-version":
      case "edit-draft":
        openVersionDraft();
        break;
      case "archive":
        runLifecycle("archive");
        break;
      case "reactivate":
        runLifecycle("reactivate");
        break;
      case "unarchive":
        runLifecycle("unarchive");
        break;
      case "retire":
        openRetireDialog();
        break;
      case "more":
        break;
      default:
        break;
    }
  }

  // —— ER-11243 version history row actions —————————————————
  // Rules 30 and 32: Correct and Void apply to any live version, the current one
  // included. Usage (Rule 29, withdrawn) is informational and never blocks.

  /** Open the Correct-this-version dialog (Figma 3572:2540). */
  function onCorrectVersion(version) {
    if (!version || rangeIsReadOnly() || version.status === "voided" || version.status === "draft") return;
    state.correctDialog = { show: true, version };
  }

  function closeCorrectDialog() {
    state.correctDialog = { show: false, version: null };
  }

  /**
   * Persist the correction in place (ER-12600). Correct returns the full
   * detail, so adopt it directly: no refetch, no Draft handoff, and the
   * version drawer stays on the corrected version.
   */
  async function confirmCorrect(payload) {
    const id = state.api?.id;
    const version = state.correctDialog.version;
    const versionId = payload?.versionId ?? version?.versionId ?? version?.id;
    if (!id || !version) return;
    if (versionId == null) {
      notifyError(new Error("CORRECT_VERSION_MISSING_ID"), "This version cannot be corrected");
      return;
    }
    if (!accountContextIsCurrent()) return;
    state.saving = true;
    try {
      const response = await correctPayRangeVersion(id, versionId, {
        effective_from: toApiDateString(payload?.startDate),
        effective_to: toApiDateString(payload?.endDate),
        min_amount: payload?.minAmount,
        max_amount: payload?.maxAmount,
      });
      if (response?.data) applyDetail(response.data, getAccountId());
      else await fetchDetail();
      notifySuccess("Version corrected");
      closeCorrectDialog();
    } catch (err) {
      notifyError(err, "Failed to correct pay range version");
    } finally {
      state.saving = false;
    }
  }

  function livePublishedCount() {
    return (versionHistory.value || []).filter(
      (row) => row.status !== "draft" && row.status !== "voided"
    ).length;
  }

  /** Open the Void confirmation dialog (ER-11598). */
  function onVoidVersion(version) {
    if (!version || rangeIsReadOnly() || version.status === "voided" || version.status === "draft") return;
    if (livePublishedCount() <= 1) {
      notifyError(null, VOID_ERROR_MESSAGES.PAY_RANGE_VERSION_LAST_REMAINING);
      return;
    }
    state.voidDialog = { show: true, version };
  }

  function closeVoidDialog() {
    state.voidDialog = { show: false, version: null };
  }

  async function confirmVoid() {
    const id = state.api?.id;
    const version = state.voidDialog.version;
    const versionId = version?.versionId ?? version?.id;
    if (!id || !version) return;
    if (versionId == null) {
      notifyError(new Error("VOID_VERSION_MISSING_ID"), "This version cannot be voided");
      return;
    }
    if (!accountContextIsCurrent()) return;
    state.saving = true;
    try {
      await voidPayRangeVersion(id, versionId, {});
      notifySuccess("Pay range version voided");
      closeVoidDialog();
      closeVersionDrawer();
      await fetchDetail();
    } catch (err) {
      notifyError(err, "Failed to void pay range version");
    } finally {
      state.saving = false;
    }
  }

  // —— ER-12492 version row ⋯ menu (new detail layout) ————————————————

  const VERSION_MENU_EVENTS = {
    edit: "edit-draft",
    publish: "publish-draft",
    discard: "discard-draft",
  };

  // The first draft's row acts on the range itself, like the header menu.
  const FIRST_DRAFT_MENU_EVENTS = {
    edit: "edit",
    publish: "publish",
    discard: "discard-draft",
  };

  /** Route a row-menu action of the new Versions list to the existing handlers. */
  function onVersionMenuAction(version, action) {
    const events =
      state.card?._meta?.status === "DRAFT" ? FIRST_DRAFT_MENU_EVENTS : VERSION_MENU_EVENTS;
    if (events[action]) {
      onCardEvent(events[action]);
      return;
    }
    const row = (versionHistory.value || []).find((item) => item.key === version?.id);
    if (!row) return;
    if (action === "correct" || action === "void") {
      if (action === "correct") onCorrectVersion(row);
      else onVoidVersion(row);
    }
  }

  // —— ER-12490 version manage drawer ———————————————————————
  // Rule 33: actions by State; Retired/Archived ranges are read-only. Rule 29's
  // lock is withdrawn, so usage never disables an action here — the backend is
  // the authority and its error is surfaced through the existing toast.

  function rangeIsReadOnly() {
    return ["RETIRED", "ARCHIVED"].includes(String(state.card?._meta?.status || "").toUpperCase());
  }

  function onOpenVersion(row) {
    if (!versionDrawerEnabled.value || !row?.key) return;
    state.versionDrawer = { show: true, key: row.key };
  }

  function closeVersionDrawer() {
    state.versionDrawer = { show: false, key: null };
  }

  function onManageCorrect(version) {
    if (!version || rangeIsReadOnly() || version.status === "voided" || version.status === "draft") return;
    state.correctDialog = { show: true, version };
  }

  function onManageVoid(version) {
    if (!version || rangeIsReadOnly() || version.status === "voided" || version.status === "draft") return;
    if (livePublishedCount() <= 1) {
      notifyError(null, VOID_ERROR_MESSAGES.PAY_RANGE_VERSION_LAST_REMAINING);
      return;
    }
    state.voidDialog = { show: true, version };
  }

  function onManageAction(event) {
    if (rangeIsReadOnly()) return;
    if (event === "new-version" || event === "edit-draft") closeVersionDrawer();
    onCardEvent(event);
  }

  // —— Chatter ————————————————————————————————
  // A version's chatter shows the range thread and that version's own thread
  // together (ER-12600). Threads page independently and merge newest-first,
  // dropping an entry when both threads carry the same message and timestamp.

  let chatterThreads = { range: null, version: null };

  function resetChatterThreads(rangeId, versionId) {
    chatterThreads = {
      range: {
        table: PAY_RANGE_CHATTER_TABLE,
        recordId: rangeId,
        page: 0,
        count: 0,
        loaded: 0,
      },
      version:
        versionId == null
          ? null
          : {
              table: PAY_RANGE_VERSION_CHATTER_TABLE,
              recordId: versionId,
              page: 0,
              count: 0,
              loaded: 0,
            },
    };
  }

  function chatterKey(item) {
    return `${item.created_on}|${item.message}`;
  }

  function addChatterRows(rows) {
    const seen = new Set(state.chatter.items.map(chatterKey));
    const merged = state.chatter.items.slice();
    transformPayRangeChatterResults(rows).forEach((item) => {
      const key = chatterKey(item);
      if (seen.has(key)) return;
      seen.add(key);
      merged.push(item);
    });
    merged.sort((a, b) => Number(b.created_on) - Number(a.created_on));
    state.chatter.items = merged;
  }

  function refreshChatterTotal() {
    const threads = [chatterThreads.range, chatterThreads.version].filter(Boolean);
    state.chatter.total = threads.reduce((sum, thread) => sum + thread.count, 0);
  }

  async function loadChatterThread(key) {
    const thread = chatterThreads[key];
    if (!thread) return;
    const accountId = getAccountId();
    if (!accountId || thread.recordId == null) return;
    // Once the thread's reported count is fully loaded there is nothing older.
    if (thread.page > 0 && thread.loaded >= thread.count) return;
    const page = thread.page + 1;
    const { data } = await getChatterList(accountId, thread.recordId, thread.table, page);
    thread.page = page;
    thread.count = Number(data?.count) || 0;
    const rows = Array.isArray(data?.results) ? data.results : [];
    thread.loaded += rows.length;
    addChatterRows(rows);
    refreshChatterTotal();
  }

  function onOpenVersionChatter(version) {
    const id = state.api?.id;
    if (id == null) return;
    const name = state.card?.name || "Pay Range";
    const versionId = version?.versionId ?? null;
    resetChatterThreads(id, versionId);
    state.chatter = {
      show: true,
      listData: { id, code: state.card?.code || "", name, status: state.card?._meta?.status },
      typeName: "Pay Range",
      typeValue: version?.versionLabel ? `${name} · ${version.versionLabel}` : name,
      iconName: "popup_pay_range",
      versionId,
      items: [],
      total: 0,
    };
    Promise.all([loadChatterThread("range"), loadChatterThread("version")]).catch((err) => {
      notifyError(err, "Failed to load pay range chatter");
    });
  }

  function closeChatterDrawer() {
    state.chatter.show = false;
  }

  async function loadMoreChatter() {
    try {
      await Promise.all([loadChatterThread("range"), loadChatterThread("version")]);
    } catch (err) {
      notifyError(err, "Failed to load more pay range chatter");
    }
  }

  return {
    state,
    versionHistory,
    fetchDetail,
    goToList,
    onCardEvent,
    onCorrectVersion,
    closeCorrectDialog,
    confirmCorrect,
    onVoidVersion,
    onVersionMenuAction,
    closeVoidDialog,
    confirmVoid,
    manageVersion,
    versionDrawerEnabled,
    onOpenVersion,
    closeVersionDrawer,
    onManageCorrect,
    onManageVoid,
    onManageAction,
    onOpenVersionChatter,
    closeChatterDrawer,
    loadMoreChatter,
    openEditModal,
    closeEditModal,
    handleEditSaveDraft,
    handleDraftDrawerSave,
    handleDraftDrawerPublish,
    onDraftDrawerSaved,
    onDraftDrawerPublished,
    closeDraftDrawer,
    discardDraft,
    openPublishDialog,
    closePublishDialog,
    confirmPublish,
    openRetireDialog,
    closeRetireDialog,
    confirmRetire,
    openVersionDraft,
    closeVersionDraft,
    handleVersionDraftSave,
    handleVersionDraftPublish,
    handleVersionDraftDiscard,
    onVersionDraftSaved,
    onVersionDraftPublished,
    onVersionDraftDiscarded,
  };
}
